/**
 * Human-readable duration for chip display. Skips zero components.
 */
export function fmtDuration(seconds) {
  const s = Math.max(0, parseInt(seconds || 0, 10));
  if (s === 0) return "0s";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (sec || parts.length === 0) parts.push(`${sec}s`);
  return parts.join(" ");
}

/**
 * Compact H / M / S trio for a single duration knob.
 * Emits total seconds via `onChange(totalSeconds)`.
 * When `readOnly` is true, renders a compact chip showing the current value.
 */
export function HMSField({ label, hint, seconds = 0, onChange, testId, presets = [], compact = false, readOnly = false, disabled = false }) {
  const s = Math.max(0, parseInt(seconds || 0, 10));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (readOnly) {
    return (
      <div>
        {label && (
          <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
        )}
        <div
          data-testid={testId}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono tabular text-slate-800 ${disabled ? "opacity-50" : ""}`}
        >
          {fmtDuration(s)}
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{hint}</div>}
      </div>
    );
  }

  const emit = (nh, nm, ns) => {
    const total = Math.max(0, parseInt(nh || 0, 10)) * 3600
      + Math.max(0, parseInt(nm || 0, 10)) * 60
      + Math.max(0, parseInt(ns || 0, 10));
    onChange(total);
  };

  const boxWide =
    "w-16 px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular text-center";
  const boxCompact =
    "w-12 px-1.5 py-1 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular text-center";
  const box = compact ? boxCompact : boxWide;
  const gap = compact ? "gap-1" : "gap-1.5";
  return (
    <div>
      {label && (
        <label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
          {label}
        </label>
      )}
      <div className={`flex items-center ${gap}`} data-testid={testId}>
        <input
          type="number" min={0} max={999}
          value={h}
          onChange={(e) => emit(e.target.value, m, sec)}
          className={box}
          aria-label={`${label} hours`}
        />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">h</span>
        <input
          type="number" min={0} max={59}
          value={m}
          onChange={(e) => emit(h, e.target.value, sec)}
          className={box}
          aria-label={`${label} minutes`}
        />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">m</span>
        <input
          type="number" min={0} max={59}
          value={sec}
          onChange={(e) => emit(h, m, e.target.value)}
          className={box}
          aria-label={`${label} seconds`}
        />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">s</span>
      </div>
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => onChange(p.seconds)}
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border transition-colors duration-150 ${
                s === p.seconds
                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      {hint && <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

export function ThresholdField({ label, hint, value, onChange, min = 0, max = 100, unit = "/100", testId, readOnly = false }) {
  if (readOnly) {
    return (
      <div>
        {label && (
          <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
        )}
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono tabular text-slate-800" data-testid={testId}>
          {value ?? 0}
          <span className="text-[10px] text-slate-500">{unit}</span>
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{hint}</div>}
      </div>
    );
  }
  return (
    <div>
      {label && (
        <label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <input
          type="number"
          data-testid={testId}
          min={min}
          max={max}
          value={value ?? 0}
          onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
          className="w-24 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
        />
        <span className="text-xs text-slate-500">{unit}</span>
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

// Resolve effective seconds from a timings doc (mirrors backend `effective_seconds`)
export function effSec(t, base, defaultSec) {
  const s = t[`${base}_seconds`];
  if (s !== null && s !== undefined) return parseInt(s, 10);
  const legacy = ({
    day1_sla:              ["day1_sla_hours", 3600],
    day2_sla:              ["day2_sla_hours", 3600],
    day3_sla:              ["day3_sla_hours", 3600],
    day4_sla:              ["day4_sla_hours", 3600],
    day5_sla:              ["day5_sla_hours", 3600],
    day6_sla:              ["day6_sla_hours", 3600],
    day7_sla:              ["day7_sla_hours", 3600],
    day8_sla:              ["day8_sla_hours", 3600],
    day9_sla:              ["day9_sla_hours", 3600],
    day10_sla:             ["day10_sla_hours", 3600],
    inbound_cooldown:      ["inbound_cooldown_hours", 3600],
    inbound_reply:         ["inbound_reply_minutes", 60],
    post_conversion_delay: ["post_conversion_delay_hours", 3600],
    win_back_delay:        ["win_back_delay_hours", 3600],
  })[base];
  if (legacy) {
    const v = t[legacy[0]];
    if (v !== null && v !== undefined) return parseInt(v, 10) * legacy[1];
  }
  return defaultSec;
}

export const SECONDS_PRESETS = [
  { label: "10s", seconds: 10 },
  { label: "30s", seconds: 30 },
  { label: "1m", seconds: 60 },
  { label: "5m", seconds: 300 },
];
export const SHORT_PRESETS = [
  { label: "10s", seconds: 10 },
  { label: "1m", seconds: 60 },
  { label: "30m", seconds: 1800 },
  { label: "2h", seconds: 7200 },
  { label: "12h", seconds: 43200 },
];
export const LONG_PRESETS = [
  { label: "10s", seconds: 10 },
  { label: "1h", seconds: 3600 },
  { label: "6h", seconds: 21600 },
  { label: "12h", seconds: 43200 },
  { label: "24h", seconds: 86400 },
];
